import React, { useEffect, useMemo, useState } from 'react';

import groupBy from 'lodash/groupBy';
import { StoryData } from 'storyblok-js-client';
import { Offer, OfferItemOneTimeRecurringPricing } from 'types/offer.types';
import { PlanContractType, PricingVariant } from 'types/plan.types';

import { useCartContext } from '@context/cart';
import { Label } from '@components/Label/Label';
import { RichText } from '@components/RichText/RichText';
import { useCartId } from '@hooks/cart';
import { useAddToCartWithOfferMutation } from '@hooks/cart/useAddToCartWithOfferMutation';
import { useCartWithOffer } from '@hooks/cart/useCartWithOffer';
import { usePhrases } from '@hooks/context/usePhrases';
import { useOfferImpressionEvent } from '@hooks/gtm/useOfferImpressionEvent';
import { useCurrencyFormatter } from '@hooks/useCurrencyFormatter';
import { useDeliveryTimeForOffer } from '@hooks/useDeliveryTimeForOffer';
import { useGlobalContent } from '@hooks/useGlobalContent';
import { useLocale } from '@hooks/useLocale';
import { useLogger } from '@hooks/useLogger';

import { convertSelectedOfferItemsToCartDto } from './helpers/converSelectedOfferItemsToCartDto';
import { sortPricingsFromLowestPrice } from './helpers/sortPricings';
import { usePricings } from './hooks/usePricings/usePricings';
import styles from './OfferCard.module.scss';
import { usePlanData } from '../../hooks/usePlanData';
import { NotActiveNotificationForOffer } from '../NotActiveNotification/NotActiveNotificationForOffer';
import { PaymentPlanFinanceBox } from './components/PaymentPlanFinanceBox/PaymentPlanFinanceBox';
import { PaymentPlanRentingBox } from './components/PaymentPlanRentingBox/PaymentPlanRentingBox';
import { MandatoryAdditionalItems } from '../AdditionalServices/MandatoryAdditionalItems';
import { OptionalAdditionalItems } from '../AdditionalServices/OptionalAdditionalItems';
import { PaymentSummary } from './components/PaymentSummary/PaymentSummary';

export const OfferCard = ({ description, paymentOptions = [], testingCategoryTitle }) => {
  const {
    offerContent: { content },
    offer,
  } = usePlanData();
  const isFinancePlan = offer.offerItems.some(offerItem => offerItem.contractType === PlanContractType.Finance);
  const { logger } = useLogger({ context: 'PDPOfferCard' });
  const deliveryTime = useDeliveryTimeForOffer(offer.groupType);
  const { currencyFormatter } = useCurrencyFormatter();
  const addToCartMutation = useAddToCartWithOfferMutation();
  const { locale } = useLocale();
  const { oneTimeFee, yourSubscription, condition, duration, readMore, monthlyFee, addToCart, financingDescription } =
    usePhrases({
      name: 'general',
    });
  const { month, oneTime } = usePhrases({ name: 'units' });
  const { optionalAdditionalServices, mandatoryAdditionalServices } = usePhrases({
    name: 'additionalServices',
    rawObject: true,
  });
  const isDemandTestCategory = offer.offerItems.some(offerItem => offerItem.isDemandTestCategory === true);
  const cartId = useCartId();
  const { refetch } = useCartWithOffer();
  const { data: globalContent } = useGlobalContent({
    locale,
  });
  const { isCartOpen } = useCartContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    if (buttonDisabled && !isCartOpen) {
      setButtonDisabled(false);
    }
  }, [isCartOpen]);

  const financingOffer = globalContent?.['financing-offer']?.global?.[0];
  const rentalOffer = globalContent?.['rental-offer']?.global?.[0];
  const offerSlug = offer.contentSlug;
  const mainItems = offer.offerItems.filter(item => item.type === 'main');
  const additionalItems = offer.offerItems.filter(item => item.type === 'additional');
  const mandatoryAdditionalItems = additionalItems.filter(
    additionalItem => additionalItem.requirementType === 'mandatory'
  );
  const optionalAdditionalItems = additionalItems.filter(
    additionalItem => additionalItem.requirementType === 'optional'
  );
  const mainItem = mainItems[0];
  const sortedPricings = sortPricingsFromLowestPrice(mainItem.pricings);
  const groupedPricings = useMemo(
    () => groupBy(sortedPricings, 'variant') as Record<PricingVariant, OfferItemOneTimeRecurringPricing[]>,
    [sortedPricings]
  );

  const {
    selectedMainProductPricing,
    selectedAdditionalServicesPricings,
    selectedAndMandatoryAdditionServicesPricings,
    additionalServicesOneTimeSum,
    additionalServicesRecurringSum,
    setSelectedMainProductPricing,
    selectAdditionalServicePricing,
  } = usePricings({
    pricings: sortedPricings,
    mandatoryAdditionalItems,
    optionalAdditionalItems,
    offerSlug,
  });

  const handleAddToCart = () => {
    logger.info({
      message: `Adding to cart - ${offerSlug} condition: ${selectedMainProductPricing.variant}, duration: ${selectedMainProductPricing.months} months`,
      params: { offerId: offerSlug, selectedMainProductPricing },
    });
    setButtonDisabled(true);
    const additionalItemsDto = convertSelectedOfferItemsToCartDto(selectedAndMandatoryAdditionServicesPricings);

    if (mainItem && selectedMainProductPricing) {
      addToCartMutation.mutate([
        { offerItemId: mainItem.id, pricingId: selectedMainProductPricing.id, contractType: mainItem.contractType },
        ...additionalItemsDto,
      ]);

      refetch();
    }
  };

  useOfferImpressionEvent(offer, selectedMainProductPricing, content.name);

  if (mainItems.length !== 1) {
    return null;
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <NotActiveNotificationForOffer
          locale={locale}
          categorySlug={mainItem.categorySlug}
          isAvailable={selectedMainProductPricing.isAvailable}
          testingCategoryTitle={testingCategoryTitle}
          isDemandTestCategory={isDemandTestCategory}
        />

        <div className={styles.description}>
          <RichText document={description} />
        </div>
        {deliveryTime && selectedMainProductPricing.isAvailable && (
          <div className={styles.deliveryTimeContainer}>
            <Label text={deliveryTime} dotColor="#8AD861" />
          </div>
        )}
      </header>

      {isFinancePlan && (
        <PaymentPlanFinanceBox
          mainItem={mainItem}
          financingOfferContent={financingOffer}
          selectedMainProductPricing={selectedMainProductPricing}
          setSelectedMainProductPricing={setSelectedMainProductPricing}
        />
      )}

      {!isFinancePlan && (
        <>
          <span className={styles.headingTitle}>{yourSubscription}</span>
          <PaymentPlanRentingBox
            readMoreText={readMore}
            conditionText={condition}
            conditionsLink={rentalOffer?.conditionsLink}
            currencyFormatter={currencyFormatter}
            groupedPricings={groupedPricings}
            selectedMainProductPricing={selectedMainProductPricing}
            setSelectedMainProductPricing={setSelectedMainProductPricing}
            durationText={duration}
            durationsLink={rentalOffer?.durationsLink}
          />
        </>
      )}

      <MandatoryAdditionalItems
        selectedMainProductPricing={selectedMainProductPricing}
        additionalItems={mandatoryAdditionalItems}
        readMore={mandatoryAdditionalServices?.readMoreText}
        additionalServicesLink={mandatoryAdditionalServices?.descriptionLink}
        additionalServicesSheetText={mandatoryAdditionalServices?.label}
        oneTime={oneTime}
        perMonth={month}
        additionalServicesText={mandatoryAdditionalServices?.label}
      />

      <OptionalAdditionalItems
        selectAdditionalServicePricing={selectAdditionalServicePricing}
        selectedAdditionalServicesPricings={selectedAdditionalServicesPricings}
        selectedMainProductPricing={selectedMainProductPricing}
        additionalItems={optionalAdditionalItems}
        readMore={optionalAdditionalServices?.readMoreText}
        additionalServicesLink={optionalAdditionalServices?.descriptionLink}
        additionalServicesSheetText={optionalAdditionalServices?.label}
        oneTime={oneTime}
        perMonth={month}
        additionalServicesText={optionalAdditionalServices?.label}
      />

      <PaymentSummary
        oneTimeFee={oneTimeFee}
        additionalServicesOneTimeSum={additionalServicesOneTimeSum}
        monthlyFeeText={monthlyFee}
        additionalServicesRecurringSum={additionalServicesRecurringSum}
        cartId={cartId}
        handleAddToCart={handleAddToCart}
        addToCartText={addToCart}
        mainItem={mainItem}
        isDemandTestCategory={isDemandTestCategory}
        paymentOptions={paymentOptions}
        buttonDisabled={buttonDisabled}
        selectedMainProductPricing={selectedMainProductPricing}
        currencyFormatter={currencyFormatter}
        isFinancePlan={isFinancePlan}
        financingDescription={financingDescription}
      />
    </div>
  );
};
